import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../../firebase/firebase";
import {
    signInWithEmailAndPassword,
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    onAuthStateChanged,
} from "firebase/auth";
import { useAnalytics } from "../../firebase/hooks/useAnalytics";
import {
    authSearch,
    readRedirect,
    redirectTarget,
} from "../../firebase/redirect";
import rightSide from "../../assets/newHome.jpg";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { trackLogin, trackPageView } = useAnalytics();

    // Helper to get language from URL
    const getLangFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    };

    const language = getLangFromUrl();
    const isEN = language === "en";

    // The page this visitor was originally after, put here by ProtectedRoute.
    // Empty for anyone who came to /login directly, which is the common case.
    const from = readRedirect(location.search);

    // Update URL when language changes
    const setLanguageInUrl = (lang) => {
        const params = new URLSearchParams(location.search);
        params.set("lang", lang);
        navigate({ search: params.toString() }, { replace: true });
    };

    useEffect(() => {
        // Track page view
        trackPageView("Login Page");
        // eslint-disable-next-line
    }, []);

    // Kept separate from the page-view effect above, and re-subscribed when the
    // language or the remembered destination changes, so the callback never
    // fires with a stale copy of either.
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                // Already signed in - send them wherever they were headed,
                // falling back to the dashboard for a direct visit.
                navigate(redirectTarget(location.search, language), {
                    replace: true,
                });
            }
        });
        return () => unsubscribe();
    }, [navigate, language, location.search]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await setPersistence(
                auth,
                rememberMe
                    ? browserLocalPersistence
                    : browserSessionPersistence,
            );
            const userCredential = await signInWithEmailAndPassword(
                auth,
                email,
                password,
            );

            // Check if email is verified
            if (!userCredential.user.emailVerified) {
                setError(
                    language === "en"
                        ? "Please verify your email address before logging in."
                        : "請在登入前驗證您的電子郵件地址。",
                );
                // Store user temporarily for verification page. The
                // destination rides along, so a newsletter recipient who has
                // never verified still lands on the report they clicked.
                navigate(`/verify-email${authSearch(language, from)}`);
                return;
            }

            // const token = await userCredential.user.getIdToken();
            // const res = await fetch("/api/initialize-user", {
            //     method: "POST", // or GET if you set it that way
            //     headers: {
            //         Authorization: `Bearer ${token}`,
            //         "Content-Type": "application/json",
            //     },
            // });

            // if (!res.ok) {
            //     throw new Error("Failed to initialize user data.");
            // }

            // await userCredential.user.getIdToken(true); // Force refresh to get latest claims

            // Track successful login
            trackLogin("email");

            navigate(redirectTarget(location.search, language), {
                replace: true,
            });
        } catch (err) {
            setError("Invalid email or password.");
        }
    };

    return (
        <div className="lg:flex h-screen font-sans relative">
            <div className="flex justify-center items-center lg:bg-slate-100 lg:w-1/2 relative z-10 min-h-screen lg:min-h-0">
                <div className="w-11/12 sm:w-3/4 lg:w-3/4 p-6 sm:p-8 bg-white/95 lg:bg-slate-100 backdrop-blur-sm lg:backdrop-blur-none rounded-xl lg:rounded-lg shadow-xl lg:shadow-none">
                    {/* Language Switcher */}
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => setLanguageInUrl("en")}
                            className={`text-sm ml-2 px-3 py-2 rounded-lg transition-all duration-200 font-medium ${
                                language === "en"
                                    ? "bg-orange-600 text-white shadow-md"
                                    : "text-gray-700 lg:text-gray-600 hover:bg-gray-200 bg-white/50 lg:bg-transparent"
                            }`}
                        >
                            English
                        </button>
                        <button
                            onClick={() => setLanguageInUrl("zh")}
                            className={`text-sm ml-2 px-3 py-2 rounded-lg transition-all duration-200 font-medium ${
                                language === "zh"
                                    ? "bg-orange-600 text-white shadow-md"
                                    : "text-gray-700 lg:text-gray-600 hover:bg-gray-200 bg-white/50 lg:bg-transparent"
                            }`}
                        >
                            繁體中文
                        </button>
                    </div>

                    {/* Logo */}
                    <div
                        onClick={() =>
                            navigate({
                                pathname: "/",
                                search: `?lang=${language}`,
                            })
                        }
                        className="flex items-center justify-center gap-6 mb-8 lg:mb-12"
                    >
                        <img
                            src="logo_tglobal.png"
                            alt="T-Global"
                            className="w-40 sm:w-48 h-auto"
                        />
                    </div>

                    {/* Login Form */}
                    <form
                        onSubmit={handleLogin}
                        className="flex flex-col space-y-4"
                    >
                        <label className="text-gray-700 font-medium">
                            {isEN ? "Email" : "電子郵件"}
                        </label>
                        <input
                            type="email"
                            placeholder={
                                isEN
                                    ? "Enter your business email"
                                    : "請輸入商業電子郵件"
                            }
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />

                        <label className="text-gray-700 font-medium">
                            {isEN ? "Password" : "密碼"}
                        </label>
                        <input
                            type="password"
                            placeholder={
                                isEN ? "Enter your password" : "請輸入密碼"
                            }
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />

                        {error && (
                            <p className="text-red-600 text-sm">* {error}</p>
                        )}

                        {/* Remember Me & Forgot Password */}
                        <div className="flex justify-between items-center mt-3 text-sm">
                            <label className="flex items-center text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={() => setRememberMe(!rememberMe)}
                                    className="mr-2 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                />
                                {isEN ? "Remember me" : "記住我"}
                            </label>
                            <button
                                type="button"
                                className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                                onClick={() => {
                                    navigate({
                                        pathname: "/forgot-password",
                                        search: `?lang=${language}`,
                                    });
                                }}
                            >
                                {isEN ? "Forgot password?" : "忘記密碼？"}
                            </button>
                        </div>

                        <button
                            type="submit"
                            className="w-full p-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-md mt-5 transition-colors duration-200"
                        >
                            {isEN ? "Log in" : "登入"}
                        </button>

                        {/* Sign Up Link */}
                        <p className="text-center mt-4 text-gray-600">
                            {isEN ? "Don't have an account?" : "還沒有帳號？"}{" "}
                            <span
                                className="text-blue-600 cursor-pointer underline hover:text-blue-800 transition-colors duration-200"
                                onClick={() =>
                                    navigate({
                                        pathname: "/Signup",
                                        search: authSearch(language, from),
                                    })
                                }
                            >
                                {isEN ? "Sign up" : "註冊"}
                            </span>
                        </p>
                    </form>
                </div>
            </div>

            <div className="lg:hidden absolute inset-0 z-0">
                <img
                    src={rightSide}
                    alt=""
                    className="w-full h-full object-cover"
                />
                {/* Dark overlay for better text readability */}
                <div className="absolute inset-0 bg-black/40"></div>
            </div>

            {/* Right Panel */}
            <div className="lg:w-1/2 hidden lg:block flex-1 bg-slate-100 overflow-hidden relative p-8">
                <img
                    src={rightSide}
                    alt=""
                    className="w-full h-full object-cover rounded-xl "
                />
            </div>
        </div>
    );
}
